import { useState } from "react";
import {
  Pencil,
  FileText,
  Layers,
  Trash2,
  X,
  ExternalLink,
  Check,
  Bell,
  Calendar as CalendarIcon,
  Phone,
  Users,
  FileText as NoteIcon,
  Link,
  Mic,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { Avatar } from "@/components/ui/Avatar";
import { CalendarEvent } from "@/types/calendar.types";
import { formatTime } from "./calendar-utils";
import { useCalendarData } from "@/hooks/calendar/useCalendarData";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { createBotFromCalendarApi } from "@/api/integrations/CreateBotFromCalendarApi";
import { cn } from "@/utils/Functions";

interface EventSheetProps {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "2-digit",
  });
}

function getParticipantName(participantId: string, getContactById: (id: string) => { name: string; email: string; avatar?: string } | undefined): string {
  if (participantId === "demo_user") return "You";
  const contact = getContactById(participantId);
  if (contact) return contact.name;
  return participantId.charAt(0).toUpperCase() + participantId.slice(1).replace(/_/g, " ");
}

function getParticipantEmail(participantId: string, getContactById: (id: string) => { name: string; email: string; avatar?: string } | undefined): string {
  if (participantId === "demo_user") return "demo@example.com";
  const contact = getContactById(participantId);
  if (contact) return contact.email;
  return `${participantId.replace(/_/g, ".")}@example.com`;
}

function getParticipantAvatar(participantId: string, getContactById: (id: string) => { name: string; email: string; avatar?: string } | undefined): string | undefined {
  const contact = getContactById(participantId);
  return contact?.avatar;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

const typeLabels: Record<CalendarEvent["type"], string> = {
  contract: "Contract Review",
  meeting: "Meeting",
  deadline: "Deadline",
  review: "Review Session",
};

export function EventSheet({ event, open, onOpenChange }: EventSheetProps) {
  const { getContactById } = useCalendarData();
  const isDemo = useIsDemo();
  const [rsvpStatus, setRsvpStatus] = useState<"yes" | "no" | "maybe" | null>(
    null
  );
  const [botStatus, setBotStatus] = useState<
    | { phase: 'idle' }
    | { phase: 'pending' }
    | { phase: 'sent'; botId: string }
    | { phase: 'error'; message: string }
  >({ phase: 'idle' });

  // UC-CAL-2 — spawn a Recall.ai meeting bot for this event. Only fires
  // outside demo mode (mock events have no real Nylas eventId). Backend
  // is /calendar/create-bot; thin client lives in
  // src/api/integrations/CreateBotFromCalendarApi.ts.
  const handleSendBot = async () => {
    if (!event || isDemo) return;
    setBotStatus({ phase: 'pending' });
    try {
      const resp = await createBotFromCalendarApi({
        eventId: event.id,
        botName: event.title,
      });
      setBotStatus({ phase: 'sent', botId: resp.botId });
    } catch (err) {
      setBotStatus({
        phase: 'error',
        message: (err as Error).message ?? 'Bot dispatch failed',
      });
    }
  };

  if (!event) return null;

  const dateStr = formatDateDisplay(event.date);
  const startTimeStr = formatTime(event.startTime);
  const endTimeStr = formatTime(event.endTime);
  const timezone = event.timezone || "EST";

  const organizer = event.participants[0] || "user1";
  const organizerName = getParticipantName(organizer, getContactById);
  const organizerEmail = getParticipantEmail(organizer, getContactById);
  const otherParticipants = event.participants.slice(1);

  const mockParticipants = [
    {
      id: organizer,
      name: organizerName,
      email: organizerEmail,
      isOrganizer: true,
      rsvp: "yes" as const,
    },
    ...otherParticipants.slice(0, 3).map((p) => ({
      id: p,
      name: getParticipantName(p, getContactById),
      email: getParticipantEmail(p, getContactById),
      isOrganizer: false,
      rsvp: "yes" as const,
    })),
    {
      id: "demo_user",
      name: "Demo User",
      email: "demo@example.com",
      isOrganizer: false,
      rsvp: rsvpStatus || ("yes" as const),
      isYou: true,
    },
  ];

  const yesCount = mockParticipants.filter((p) => p.rsvp === "yes").length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "w-full sm:max-w-[560px] overflow-y-auto p-0 border-l border-r border-t",
          "data-starting-style:translate-x-full data-ending-style:translate-x-full",
          "transition-transform duration-500 ease-out-expo"
        )}
        showCloseButton={false}
      >
        <div className="flex flex-col h-full">
          <SheetHeader className="px-4 pt-4 pb-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                >
                  <Pencil className="size-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                >
                  <FileText className="size-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                >
                  <Layers className="size-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                >
                  <Trash2 className="size-4 text-muted-foreground" />
                </Button>
              </div>
              <SheetClose
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 rounded-full bg-muted hover:bg-muted"
                  >
                    <X className="size-4 text-muted-foreground" />
                  </Button>
                }
              />
            </div>

            <div className="flex flex-col gap-1 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide px-1.5 py-0.5 bg-muted rounded">
                  {typeLabels[event.type]}
                </span>
              </div>
              <SheetTitle className="text-xl font-semibold text-foreground leading-normal">
                {event.title}
              </SheetTitle>
              <div className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
                <span>{dateStr}</span>
                <span className="size-1 rounded-full bg-muted-foreground" />
                <span>
                  {startTimeStr} - {endTimeStr}
                </span>
                <span className="size-1 rounded-full bg-muted-foreground" />
                <span>{timezone}</span>
              </div>
            </div>

            <Button variant="outline">
              <span>Propose new time</span>
              <ExternalLink className="size-4" />
            </Button>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="flex flex-col gap-4 max-w-[512px] mx-auto">
              <div className="flex flex-col gap-4">
                {mockParticipants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-start gap-3 relative"
                  >
                    <Avatar
                      className="size-7 border-[1.4px] border-background shrink-0"
                      src={getParticipantAvatar(participant.id, getContactById)}
                      fallback={participant.name.charAt(0)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 relative">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1 relative">
                            <p className="text-[13px] font-medium text-foreground leading-[18px]">
                              {participant.name}
                            </p>
                            {participant.isOrganizer && (
                              <span className="text-[10px] font-medium text-cyan-500 px-0.5 py-0.5 rounded-full">
                                Organizer
                              </span>
                            )}
                            {participant.isYou && (
                              <span className="text-[10px] font-medium text-foreground px-0.5 py-0.5 rounded-full">
                                You
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground leading-none">
                            {participant.email}
                          </p>
                        </div>
                        <Check className="size-3 text-green-500 shrink-0 absolute right-0 top-[17px]" />
                      </div>
                      {participant.isYou && (
                        <div className="mt-3 flex gap-1.5 bg-muted/50 rounded-lg p-1.5">
                          <Button
                            variant={rsvpStatus === "yes" ? "default" : "ghost"}
                            size="sm"
                            className={`flex-1 h-[30px] text-xs font-medium ${
                              rsvpStatus === "yes"
                                ? "bg-foreground text-background hover:bg-foreground/90 shadow-sm"
                                : "text-muted-foreground"
                            }`}
                            onClick={() => setRsvpStatus("yes")}
                          >
                            Yes
                          </Button>
                          <Button
                            variant={rsvpStatus === "no" ? "default" : "ghost"}
                            size="sm"
                            className={`flex-1 h-[30px] text-xs font-medium ${
                              rsvpStatus === "no"
                                ? "bg-foreground text-background hover:bg-foreground/90 shadow-sm"
                                : "text-muted-foreground"
                            }`}
                            onClick={() => setRsvpStatus("no")}
                          >
                            No
                          </Button>
                          <Button
                            variant={
                              rsvpStatus === "maybe" ? "default" : "ghost"
                            }
                            size="sm"
                            className={`flex-1 h-[30px] text-xs font-medium ${
                              rsvpStatus === "maybe"
                                ? "bg-foreground text-background hover:bg-foreground/90 shadow-sm"
                                : "text-muted-foreground"
                            }`}
                            onClick={() => setRsvpStatus("maybe")}
                          >
                            Maybe
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {event.meetingLink && (
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="size-6 shrink-0">
                      <svg
                        viewBox="0 0 24 24"
                        className="size-full"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"
                          fill="#22C55E"
                        />
                      </svg>
                    </div>
                    <p className="text-xs font-medium text-muted-foreground flex-1">
                      Meeting Link Available
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 h-8 bg-foreground text-background hover:bg-foreground/90 text-xs font-medium gap-2 shadow-sm"
                      onClick={() => {
                        if (event.meetingLink) {
                          window.open(event.meetingLink, "_blank");
                        }
                      }}
                    >
                      <span>Join Meeting</span>
                      <ExternalLink className="size-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-2 text-xs border-border"
                      onClick={() => {
                        if (event.meetingLink) {
                          copyToClipboard(event.meetingLink);
                        }
                      }}
                    >
                      <Link className="size-4" />
                      <span>Copy link</span>
                    </Button>
                  </div>
                  {/* UC-CAL-2 — Send Ari to record. Only renders outside
                      demo mode because mock events have no real Nylas
                      eventId for /calendar/create-bot to look up. */}
                  {!isDemo && (
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-2 text-xs border-border w-full"
                        onClick={handleSendBot}
                        disabled={botStatus.phase === 'pending' || botStatus.phase === 'sent'}
                      >
                        <Mic className="size-4" />
                        <span>
                          {botStatus.phase === 'pending'
                            ? 'Dispatching Ari…'
                            : botStatus.phase === 'sent'
                              ? 'Ari is on the way'
                              : 'Send Ari to record'}
                        </span>
                      </Button>
                    </div>
                  )}
                  {botStatus.phase === 'error' && (
                    <p className="text-xs text-red-400 mt-1" role="alert">
                      {botStatus.message}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1">
                    <Bell className="size-4" />
                  </div>
                  <span>Reminder: 30min before</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1">
                    <CalendarIcon className="size-4" />
                  </div>
                  <span>Organizer: {organizerEmail}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1">
                    <Phone className="size-4" />
                  </div>
                  <span>(US) +1 904-330-1131</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1">
                    <Users className="size-4" />
                  </div>
                  <span>
                    {mockParticipants.length} persons
                    <span className="mx-1">&bull;</span>
                    {yesCount} yes
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1">
                    <NoteIcon className="size-4" />
                  </div>
                  <span>Notes from Organizer</span>
                </div>
              </div>

              {event.description && (
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground leading-[1.6]">
                    {event.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
